import { motion } from 'framer-motion';
import { Link } from 'wouter';
import { Calendar, Users, TrendingUp, MapPin, BarChart3 } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useAdminDashboardStats } from '../api/useAdminDashboard';
import { NextEventsTimer } from '../components';
import { Loader } from '@/components/Loader';

// Color palette for different cities in charts
const CITY_COLORS = [
  '#8B5CF6', // purple
  '#06B6D4', // cyan
  '#F59E0B', // amber
  '#10B981', // emerald
  '#EF4444', // red
  '#EC4899', // pink
  '#6366F1', // indigo
  '#84CC16', // lime
];

export default function AdminDashboard() {
  const { data: statsData, isLoading } = useAdminDashboardStats();

  const stats = statsData?.data;
  const usersPerCity = stats?.usersPerCity || [];
  const bookingsPerCityEvent = stats?.bookingsPerCityEvent || [];
  const signupsPerDay = stats?.signupsPerDay || [];

  // Transform bookings data for stacked bar chart
  const transformBookingsForChart = () => {
    const dateMap = new Map<string, Record<string, number>>();
    const citySet = new Set<string>();

    bookingsPerCityEvent.forEach((item) => {
      citySet.add(item.cityName);
      if (!dateMap.has(item.date)) {
        dateMap.set(item.date, {});
      }
      const dateEntry = dateMap.get(item.date)!;
      dateEntry[item.cityName] = item.count;
    });

    const cities = Array.from(citySet);
    const chartData = Array.from(dateMap.entries())
      .map(([date, cityData]) => ({
        date,
        ...cityData,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return { chartData, cities };
  };

  const { chartData: bookingsChartData, cities: bookingsCities } = transformBookingsForChart();

  // Calculate totals
  const totalUsers = usersPerCity.reduce((sum, c) => sum + c.totalUsers, 0);
  const totalSignupsLastWeek = usersPerCity.reduce((sum, c) => sum + c.signupsLastWeek, 0);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-4xl md:text-5xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-dark-muted text-lg">
          User growth and event analytics
        </p>
      </motion.div>

      {/* Next Events Countdown */}
      <NextEventsTimer />


      {/* Quick Stats Grid */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalUsers.toLocaleString()}</p>
              <p className="text-dark-muted text-sm">Total Users</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-accent-500/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-accent-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">+{totalSignupsLastWeek.toLocaleString()}</p>
              <p className="text-dark-muted text-sm">Signups This Week</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{usersPerCity.length}</p>
              <p className="text-dark-muted text-sm">Active Cities</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Users Per City Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card mb-8"
      >
        <h2 className="text-2xl font-bold mb-4">Users Per City</h2>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-dark-card">
                <tr className="border-b border-dark-card dark:bg-black bg-white">
                  <th className="text-left py-3 px-4 text-dark-muted font-medium">City</th>
                  <th className="text-right py-3 px-4 text-dark-muted font-medium">Total Users</th>
                  <th className="text-right py-3 px-4 text-dark-muted font-medium">Subscribers</th>
                  <th className="text-right py-3 px-4 text-dark-muted font-medium">Paid</th>
                  <th className="text-right py-3 px-4 text-dark-muted font-medium">Last 24h</th>
                  <th className="text-right py-3 px-4 text-dark-muted font-medium">Last 7 days</th>
                  <th className="text-right py-3 px-4 text-dark-muted font-medium">Last 30 days</th>
                </tr>
              </thead>
              <tbody>
                {usersPerCity.map((city, index) => (
                  <tr 
                    key={city.cityId} 
                    className="border-b border-dark-card/50 hover:bg-dark-card/30 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: CITY_COLORS[index % CITY_COLORS.length] }}
                        />
                        <span className="font-medium">{city.cityName}</span>
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 font-semibold">
                      {city.totalUsers.toLocaleString()}
                    </td>
                    <td className="text-right py-3 px-4">
                      <span className="text-primary-400 font-medium">
                        {city.activeSubscribers.toLocaleString()}
                      </span>
                    </td>
                    <td className="text-right py-3 px-4">
                      <span className="text-accent-400 font-medium">
                        {city.paidUsers.toLocaleString()}
                      </span>
                    </td>
                    <td className="text-right py-3 px-4">
                      <span className={city.signupsLastDay > 0 ? 'text-green-400' : 'text-dark-muted'}>
                        {city.signupsLastDay > 0 ? '+' : ''}{city.signupsLastDay}
                      </span>
                    </td>
                    <td className="text-right py-3 px-4">
                      <span className={city.signupsLastWeek > 0 ? 'text-green-400' : 'text-dark-muted'}>
                        {city.signupsLastWeek > 0 ? '+' : ''}{city.signupsLastWeek}
                      </span>
                    </td>
                    <td className="text-right py-3 px-4">
                      <span className={city.signupsLastMonth > 0 ? 'text-green-400' : 'text-dark-muted'}>
                        {city.signupsLastMonth > 0 ? '+' : ''}{city.signupsLastMonth}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Bookings Per City & Event Day Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card mb-8"
      >
        <h2 className="text-2xl font-bold mb-4">Bookings Per City & Event Day</h2>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : bookingsChartData.length === 0 ? (
          <div className="flex justify-center py-12 text-dark-muted">
            No booking data available
          </div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bookingsChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF' }}
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                />
                <YAxis
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '0.5rem',
                    color: '#F9FAFB'
                  }}
                  labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                    timeZone: 'UTC',
                  })}
                />
                <Legend />
                {bookingsCities.map((city, index) => (
                  <Bar
                    key={city}
                    dataKey={city}
                    stackId="bookings"
                    fill={CITY_COLORS[index % CITY_COLORS.length]}
                    name={city}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </motion.div>

      {/* User Signups Per Day Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card mb-8"
      >
        <h2 className="text-2xl font-bold mb-4">User Signups Per Day (Last 30 Days)</h2>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={signupsPerDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="date"
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF' }}
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                />
                <YAxis
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF' }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '0.5rem',
                    color: '#F9FAFB'
                  }}
                  labelFormatter={(date) => new Date(date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                    timeZone: 'UTC',
                  })}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#8B5CF6" 
                  strokeWidth={2}
                  dot={{ fill: '#8B5CF6', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Signups"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <h2 className="text-2xl font-bold mb-4">Quick Actions</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link href="/admin/events" className="card hover:border-primary-500/50 transition-colors block">
            <Calendar className="w-8 h-8 text-primary-500 mb-3" />
            <h3 className="text-lg font-semibold mb-1">Manage Events</h3>
            <p className="text-dark-muted text-sm">Create and manage dinner events</p>
          </Link>

          <Link href="/admin/users" className="card hover:border-primary-500/50 transition-colors block">
            <Users className="w-8 h-8 text-accent-500 mb-3" />
            <h3 className="text-lg font-semibold mb-1">View Users</h3>
            <p className="text-dark-muted text-sm">Browse user profiles and data</p>
          </Link>

          <Link href="/admin/polls" className="card hover:border-primary-500/50 transition-colors block">
            <TrendingUp className="w-8 h-8 text-primary-500 mb-3" />
            <h3 className="text-lg font-semibold mb-1">Manage Polls</h3>
            <p className="text-dark-muted text-sm">Create and view poll results</p>
          </Link>

          <Link href="/admin/analytics" className="card hover:border-primary-500/50 transition-colors block">
            <BarChart3 className="w-8 h-8 text-cyan-500 mb-3" />
            <h3 className="text-lg font-semibold mb-1">Analytics</h3>
            <p className="text-dark-muted text-sm">Advanced signup & payment analytics</p>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
